import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, getDoc, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { ArrowLeft, CheckCircle, XCircle, User, Calendar, Building2 } from 'lucide-react';
import Swal from 'sweetalert2';
import type { Unit } from '@/types';

interface PendingUnit extends Omit<Unit, 'typeId'> {
  createdBy: string;
  createdByEmail: string;
  typeId?: string;
  reviewData?: {
    submittedAt: Date;
    submittedBy: string;
    submittedByEmail: string;
  };
}

interface UserDetails {
  uid: string;
  name: string;
  email: string;
  rank?: string;
}

interface UnitType {
  id: string;
  name: string;
  entityName: string;
  ceremonies?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export default function AdminUnitReviewsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedUnitId = searchParams.get('unit');
  
  const [loading, setLoading] = useState(true);
  const [pendingUnits, setPendingUnits] = useState<PendingUnit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<PendingUnit | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [processing, setProcessing] = useState(false);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);

  // Helper function to get unit type information
  const getUnitTypeInfo = (typeId: string) => {
    return unitTypes.find(type => type.id === typeId) || null;
  };

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsQuery = query(
          collection(db, 'systemSettings'),
          limit(1)
        );
        const settingsSnapshot = await getDocs(settingsQuery);
        
        if (!settingsSnapshot.empty) {
          const settings = settingsSnapshot.docs[0].data();
          if (settings.unitTypes && Array.isArray(settings.unitTypes)) {
            setUnitTypes(settings.unitTypes);
          }
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  // Fetch pending units
  useEffect(() => {
    const fetchPendingUnits = async () => {
      try {
        const q = query(
          collection(db, 'units'),
          where('reviewStatus', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const units: PendingUnit[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name,
            number: data.unitNumber || data.number,
            typeId: data.typeId,
            members: data.members,
            createdAt: data.createdAt?.toDate() || new Date(),
            settings: data.settings || {},
            reviewStatus: data.reviewStatus,
            reviewData: data.reviewData ? {
              submittedAt: data.reviewData.submittedAt?.toDate() || new Date(),
              submittedBy: data.reviewData.submittedBy,
              submittedByEmail: data.reviewData.submittedByEmail,
            } : undefined,
            createdBy: data.createdBy,
            createdByEmail: data.createdByEmail,
          } as PendingUnit;
        });

        setPendingUnits(units);

        // If there's a selected unit ID, load it
        if (selectedUnitId) {
          const unit = units.find((l) => l.id === selectedUnitId);
          if (unit) {
            setSelectedUnit(unit);
          }
        }
      } catch (error) {
        console.error('Error fetching pending units:', error);
        showError('Failed to load pending units');
      } finally {
        setLoading(false);
      }
    };

    fetchPendingUnits();
  }, [selectedUnitId]);

  // Fetch user details when a unit is selected
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!selectedUnit) {
        setUserDetails(null);
        return;
      }

      // Use email from unit data (from auth) as primary source, with fallback to reviewData
      const emailFromAuth = selectedUnit.createdByEmail || selectedUnit.reviewData?.submittedByEmail || '';
      
      try {
        // Try to fetch additional user details from user document
        const userDoc = await getDoc(doc(db, 'users', selectedUnit.createdBy));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserDetails({
            uid: userDoc.id,
            name: userData.name || '',
            email: emailFromAuth || userData.email || '',
            rank: userData.rank,
          });
        } else {
          // Fallback if user document doesn't exist
          setUserDetails({
            uid: selectedUnit.createdBy,
            name: 'Unknown User',
            email: emailFromAuth,
            rank: undefined,
          });
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        // Fallback with just auth email
        setUserDetails({
          uid: selectedUnit.createdBy,
          name: 'Unknown User',
          email: emailFromAuth,
          rank: undefined,
        });
      }
    };

    fetchUserDetails();
  }, [selectedUnit]);

  const handleApproveUnit = async () => {
    if (!selectedUnit) return;

    const result = await Swal.fire({
      title: 'Approve Unit?',
      html: `
        <div class="text-left">
          <p class="mb-3">Are you sure you want to approve this unit?</p>
          <div class="bg-blue-50 p-4 rounded-lg">
            <p class="font-semibold text-blue-900">${selectedUnit.name}</p>
            <p class="text-sm text-blue-700">${selectedUnit.number}</p>
          </div>
          <p class="mt-4 text-sm text-gray-600">
            The user will be notified via email and gain full access to the unit.
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6c757d',
    });

    if (!result.isConfirmed) return;

    setProcessing(true);
    try {
      const approveUnit = httpsCallable(functions, 'approveUnit');
      await approveUnit({ unitId: selectedUnit.id });

      showSuccess('Unit approved successfully!');
      
      // Remove from pending list and clear selection
      setPendingUnits(pendingUnits.filter((l) => l.id !== selectedUnit.id));
      setSelectedUnit(null);
      setUserDetails(null);
    } catch (error: any) {
      console.error('Error approving unit:', error);
      showError(error.message || 'Failed to approve unit');
    } finally {
      setProcessing(false);
    }
  };

  const handleDenyUnit = async () => {
    if (!selectedUnit) return;

    const result = await Swal.fire({
      title: 'Deny Unit Application',
      html: `
        <div class="text-left">
          <p class="mb-3">Please provide a reason for denying this unit application:</p>
          <div class="bg-gray-100 p-3 rounded mb-4">
            <p class="font-semibold">${selectedUnit.name}</p>
            <p class="text-sm text-gray-600">${selectedUnit.number}</p>
          </div>
        </div>
      `,
      input: 'textarea',
      inputPlaceholder: 'Enter the reason for denial...',
      inputAttributes: {
        'aria-label': 'Denial reason'
      },
      showCancelButton: true,
      confirmButtonText: 'Deny Unit',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6c757d',
      inputValidator: (value) => {
        if (!value) {
          return 'You must provide a reason!';
        }
      }
    });

    if (!result.isConfirmed || !result.value) return;

    setProcessing(true);
    try {
      const denyUnit = httpsCallable(functions, 'denyUnit');
      await denyUnit({ 
        unitId: selectedUnit.id,
        reason: result.value 
      });

      showSuccess('Unit application denied');
      
      // Remove from pending list and clear selection
      setPendingUnits(pendingUnits.filter((l) => l.id !== selectedUnit.id));
      setSelectedUnit(null);
      setUserDetails(null);
    } catch (error: any) {
      console.error('Error denying unit:', error);
      showError(error.message || 'Failed to deny unit');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main className="flex-1 px-4 py-8">
          <div className="container mx-auto max-w-6xl">
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading pending units...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Unit Reviews</h1>
            <p className="text-muted-foreground">
              Review and approve or deny pending unit applications
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Pending Units List */}
            <div className="md:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Units ({pendingUnits.length})</CardTitle>
                  <CardDescription>Click to review</CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingUnits.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      No pending units to review
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {pendingUnits.map((unit) => (
                        <button
                          key={unit.id}
                          onClick={() => setSelectedUnit(unit)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedUnit?.id === unit.id
                              ? 'bg-primary/10 border-primary'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          <p className="font-semibold text-sm">{unit.name}</p>
                          <p className="text-xs text-muted-foreground">{unit.number}</p>
                          {unit.typeId && (() => {
                            const unitType = getUnitTypeInfo(unit.typeId);
                            return unitType ? (
                              <p className="text-xs text-blue-600 font-medium">
                                {unitType.name} ({unitType.entityName})
                              </p>
                            ) : null;
                          })()}
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(unit.createdAt).toLocaleDateString()}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Unit Details and Actions */}
            <div className="md:col-span-2">
              {selectedUnit ? (
                <div className="space-y-6">
                  {/* Unit Information */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{selectedUnit.name}</CardTitle>
                          <CardDescription>{selectedUnit.number}</CardDescription>
                        </div>
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          Pending Review
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4">
                        {/* Unit Type Information */}
                        {selectedUnit.typeId && (() => {
                          const unitType = getUnitTypeInfo(selectedUnit.typeId);
                          return unitType ? (
                            <div className="flex items-center gap-2 text-sm">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Type:</span>
                              <span className="font-medium">{unitType.name}</span>
                              <span className="text-muted-foreground">({unitType.entityName})</span>
                            </div>
                          ) : null;
                        })()}
                        
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Submitted:</span>
                          <span className="font-medium">
                            {selectedUnit.reviewData?.submittedAt.toLocaleString('en-GB', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* User Information */}
                  {userDetails && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <User className="h-5 w-5" />
                          User Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-3">
                          <div className="grid grid-cols-2">
                            <span className="text-sm text-muted-foreground">Name:</span>
                            <span className="text-sm font-medium">{userDetails.name}</span>
                          </div>
                          <div className="grid grid-cols-2">
                            <span className="text-sm text-muted-foreground">Email:</span>
                            <a 
                              href={`mailto:${userDetails.email}`}
                              className="text-sm font-medium text-primary hover:underline break-all"
                            >
                              {userDetails.email}
                            </a>
                          </div>
                          {userDetails.rank && (
                            <div className="grid grid-cols-2">
                              <span className="text-sm text-muted-foreground">Rank:</span>
                              <span className="text-sm font-medium">{userDetails.rank}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Review Actions</CardTitle>
                      <CardDescription>
                        Approve or deny this unit application
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-3">
                        <Button
                          onClick={handleApproveUnit}
                          disabled={processing}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Approve Unit
                        </Button>
                        <Button
                          onClick={handleDenyUnit}
                          disabled={processing}
                          variant="destructive"
                          className="flex-1"
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Deny Unit
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12">
                    <p className="text-center text-muted-foreground">
                      Select a unit from the list to review
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

